import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { emptyUntilLoaded } from '../../store/empty_slice_state';
import { destroySamlSession } from '../../utils/saml';
import { appAuthDone, appInstanceCreated } from './external/slice';
import type { AuthAppState, AuthDomainListEntry, AuthUserData } from './types';

const initialState: AuthAppState = {
  validationErrors: [],
  forgotPasswordErrors: [],
  resetPasswordErrors: [],
  userData: emptyUntilLoaded<AuthUserData>(),
  originalUserId: undefined,
  appInstanceAdded: false,
  authDomainList: [],
  loginEmail: '',
  loginType: 'simple',
};

// @deprecated This should be replaced with shared-lib auth reducer and
// additional iconik web specific states should be decoupled to authExtra reducer.
export const authSlice = createSlice({
  name: 'authReducer',
  initialState,
  reducers: {
    startLogin: (state, action: PayloadAction<'simple' | 'saml'>) => {
      if (action.payload === 'simple') {
        destroySamlSession();
      }
      state.loginType = action.payload;
    },
    endLogin: (_state, _action: PayloadAction<'simple' | 'saml'>) => {},
    loginError: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload ?? [];
    },
    setCurrentUser: (state, action: PayloadAction<AuthUserData>) => {
      state.userData = action.payload;
    },
    setCurrentUserId: (
      _state,
      _action: PayloadAction<string | undefined>,
    ) => {},
    setOriginalUserId: (state, action: PayloadAction<string | undefined>) => {
      state.originalUserId = action.payload;
    },
    logout: () => {
      destroySamlSession();
      return initialState;
    },
    setAuthDomainList: (
      state,
      action: PayloadAction<AuthDomainListEntry[]>,
    ) => {
      state.authDomainList = action.payload;
    },
    setLoginEmail: (state, action: PayloadAction<string>) => {
      state.loginEmail = action.payload;
    },
    forgotPassword: (state) => {
      state.resetPasswordSent = true;
      state.forgotPasswordErrors = [];
    },
    clearResetPasswordData: (state) => {
      state.forgotPasswordErrors = [];
      state.resetPasswordSent = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(appInstanceCreated, (state) => {
        state.appInstanceAdded = true;
      })
      .addCase(appAuthDone, (state) => {
        state.appInstanceAdded = false;
      });
  },
});

export const {
  startLogin,
  endLogin,
  loginError,
  setCurrentUser,
  setCurrentUserId,
  setOriginalUserId,
  logout,
  setAuthDomainList,
  setLoginEmail,
  forgotPassword,
  clearResetPasswordData,
} = authSlice.actions;

export const authReducer = authSlice.reducer;
